import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Mail, Globe, Sparkles, FileSpreadsheet, Reply, Inbox, Paperclip, ShieldCheck, Eye, Clock, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { CompanyLogo, SearchField } from "@/components/common";
import { ago } from "@/lib/sk";
import { api } from "@/lib/api";
import { useResource, mutate } from "@/hooks/use-resource";
import { Panel, IconButton } from "@/components/layout/Panel";

export const TYPES = {
  request: { label: "Forespørsel", icon: Inbox, cls: "bg-[hsl(var(--brand)/0.08)] text-[hsl(var(--brand-strong))]" },
  reply: { label: "Kundesvar", icon: Reply, cls: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]" },
  approval: { label: "Godkjenning", icon: ShieldCheck, cls: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]" },
  quote: { label: "Tilbud åpnet", icon: Eye, cls: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]" },
  followup: { label: "Oppfølging", icon: Clock, cls: "bg-[hsl(var(--v-price-bg))] text-[hsl(var(--v-price))]" },
};
export const SOURCE = { email: ["E-post", Mail], web_form: ["Webskjema", Globe], assistant: ["Assistent", Sparkles] };
export const sourceOf = (p) => (p?.intake ? "assistant" : p?.source === "email" ? "email" : "web_form");
export const FILTERS = [["all", "Alle"], ["unread", "Uleste"], ["request", "Forespørsler"], ["reply", "Kundesvar"], ["approval", "Godkjenninger"], ["quote", "Tilbud"], ["followup", "Oppfølging"]];
export const INBOX_URL = "/inbox";
export const keyPath = (key) => `/innboks/${encodeURIComponent(key)}`;

export function useInbox() {
  return useResource("/inbox", null, { interval: 45000 });
}

export async function markSeen(keys, seen = true) {
  mutate(INBOX_URL, (d) => (d ? { ...d, items: d.items.map((x) => (keys.includes(x.key) ? { ...x, seen } : x)), unread: d.items.filter((x) => (keys.includes(x.key) ? !seen : !x.seen)).length } : d));
  await api.post(seen ? "/inbox/seen" : "/inbox/unseen", { keys });
}

export async function archiveItems(keys) {
  mutate(INBOX_URL, (d) => (d ? { ...d, items: d.items.filter((x) => !keys.includes(x.key)), unread: d.items.filter((x) => !keys.includes(x.key) && !x.seen).length } : d));
  await api.post("/inbox/archive", { keys });
}

export function filterItems(items, filter, q) {
  const s = (q || "").trim().toLowerCase();
  return (items || []).filter((x) => {
    if (filter === "unread" && x.seen) return false;
    if (!["all", "unread"].includes(filter) && x.type !== filter) return false;
    if (!s) return true;
    const p = x.project || {};
    return [x.title, x.subtitle, x.preview, p.name, p.customer?.name, p.contact?.name, p.vessel?.name, p.vessel?.type, p.email?.subject].some((v) => (v || "").toLowerCase().includes(s));
  });
}

function groupByTime(rows) {
  const now = Date.now();
  const day = 86400000;
  const b = [["I dag", []], ["I går", []], ["Denne uken", []], ["Tidligere", []]];
  rows.forEach((it) => { const age = now - new Date(it.at).getTime(); b[age < day ? 0 : age < day * 2 ? 1 : age < day * 7 ? 2 : 3][1].push(it); });
  return b.filter(([, l]) => l.length);
}

export function TypeChip({ item, className }) {
  const p = item.project || {};
  if (item.type === "request") {
    const [label, Icon] = SOURCE[sourceOf(p)];
    return <span className={cn("inline-flex items-center gap-1 rounded-md bg-black/[0.05] px-1.5 py-0.5 text-[10.5px] font-semibold text-foreground/65", className)}><Icon className="h-3 w-3" /> {label}</span>;
  }
  const t = TYPES[item.type];
  const Icon = t.icon;
  return <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold", t.cls, className)}><Icon className="h-3 w-3" /> {t.label}</span>;
}

function Row({ it, active, onSelect }) {
  const p = it.project || {};
  return (
    <button type="button" onClick={() => onSelect(it)} aria-current={active ? "true" : undefined} data-unread={it.seen ? "false" : "true"} data-testid={`inbox-row-${it.key}`}
      className={cn("group relative flex w-full items-start gap-2.5 rounded-[12px] py-2.5 pl-3 pr-2.5 text-left outline-none transition-[background-color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40", active ? "bg-white shadow-[0_1px_2px_hsl(var(--shadow-color)/0.06),0_0_0_1px_hsl(var(--shadow-color)/0.06)]" : "hover:bg-black/[0.04]")}>
      <span className={cn("absolute left-0.5 top-[19px] h-1.5 w-1.5 rounded-full bg-primary transition-opacity", it.seen ? "opacity-0" : "opacity-100")} aria-hidden />
      <CompanyLogo name={p.customer?.name} domain={p.customer?.website} size="md" className="mt-px h-8 w-8 rounded-[9px] p-1" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn("truncate text-[13px]", it.seen ? "font-medium text-foreground/80" : "font-semibold text-foreground")}>{it.subtitle || p.contact?.name || "Ukjent avsender"}</span>
          <span className={cn("shrink-0 font-mono text-[10.5px] tabular-nums", it.seen ? "text-muted-foreground/70" : "text-primary")}>{ago(it.at)}</span>
        </span>
        <span className={cn("mt-0.5 block truncate text-[12.5px]", it.seen ? "text-foreground/70" : "text-foreground")}>{it.title}</span>
        <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{it.preview}</span>
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <TypeChip item={it} />
          {it.type === "request" && p.replied_at ? <span className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--v-ok-bg))] px-1.5 py-0.5 text-[10.5px] font-semibold text-[hsl(var(--v-ok))]"><Reply className="h-3 w-3" /> Besvart</span> : null}
          {it.type === "request" && p.valves_count ? <span className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--brand-soft))] px-1.5 py-0.5 text-[10.5px] font-semibold text-primary"><FileSpreadsheet className="h-3 w-3" /> {p.valves_count} ventiler</span> : null}
          {it.type === "request" && !p.valves_count && p.files?.length ? <span className="inline-flex items-center gap-1 rounded-md bg-black/[0.05] px-1.5 py-0.5 text-[10.5px] font-semibold text-foreground/65"><Paperclip className="h-3 w-3" /> {p.files.length}</span> : null}
        </span>
      </span>
    </button>
  );
}

/** The inbox list – lives in the context panel on desktop and is the page on mobile. */
export function InboxList({ filter, setFilter, q, setQ, onSelect, className }) {
  const { data, loading } = useInbox();
  const { key } = useParams();
  const selected = key ? decodeURIComponent(key) : null;
  const items = useMemo(() => filterItems(data?.items, filter, q), [data, filter, q]);
  const groups = useMemo(() => groupByTime(items), [items]);
  const counts = data?.counts || {};
  const filters = FILTERS.filter(([k]) => ["all", "unread"].includes(k) || counts[k]);
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)} data-testid="inbox-list">
      <div className="shrink-0 px-3">
        <SearchField value={q} onChange={setQ} placeholder="Søk i innboksen…" inputClassName="h-9 rounded-xl border-black/[0.06] bg-white text-[13px]" testId="inbox-search-input" />
        <div className="mt-2.5 flex flex-wrap gap-1 pb-1" role="tablist" aria-label="Filter">
          {filters.map(([k, label]) => {
            const n = k === "unread" ? data?.unread : k === "all" ? null : counts[k];
            return (
              <button key={k} type="button" role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={cn("flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-[12px] font-medium transition-colors", filter === k ? "bg-[hsl(var(--brand))] text-white" : "bg-black/[0.045] text-foreground/70 hover:bg-black/[0.07] hover:text-foreground")} data-testid={`inbox-filter-${k}`}>
                {label}{n ? <span className="font-mono text-[10.5px] tabular-nums opacity-75">{n}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
      <div className="scroll-fade-b min-h-0 flex-1 overflow-y-auto px-2 pb-6 pt-1 scrollbar-thin">
        {loading ? (
          <div className="space-y-1.5 px-1 pt-2">{[0, 1, 2, 3, 4].map((k) => <div key={k} className="shimmer h-[88px] rounded-[12px]" />)}</div>
        ) : items.length ? (
          groups.map(([label, list], gi) => (
            <div key={label} className={cn(gi > 0 && "mt-2")}>
              <div className="sticky top-0 z-10 bg-[hsl(var(--sidebar))] px-3 pb-1.5 pt-2 text-[11.5px] font-medium text-muted-foreground/70">{label}</div>
              <div className="space-y-0.5">{list.map((it) => <Row key={it.key} it={it} active={it.key === selected} onSelect={onSelect} />)}</div>
            </div>
          ))
        ) : (
          <div className="px-4 py-14 text-center" data-testid="inbox-empty">
            <CheckCheck className="mx-auto h-6 w-6 text-primary" />
            <div className="mt-3 text-[14px] font-medium">{q || filter !== "all" ? "Ingen treff" : "Alt er tatt hånd om"}</div>
            <p className="mx-auto mt-1 max-w-[220px] text-[12.5px] text-muted-foreground">{q || filter !== "all" ? "Prøv et annet filter eller søkeord." : "Nye forespørsler, kundesvar og godkjenninger dukker opp her."}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const FILTER_KEY = "sk.inbox.filter";
const fstate = { filter: sessionStorage.getItem(FILTER_KEY) || "all", q: "" };
const fsubs = new Set();
const fset = (patch) => { Object.assign(fstate, patch); if (patch.filter) sessionStorage.setItem(FILTER_KEY, patch.filter); fsubs.forEach((fn) => fn((n) => n + 1)); };

/** Filter + search shared between the panel list and the page (so J/K follow what you see). */
export function useInboxFilter() {
  const [, force] = React.useState(0);
  React.useEffect(() => { fsubs.add(force); return () => fsubs.delete(force); }, []);
  return { filter: fstate.filter, q: fstate.q, setFilter: (filter) => fset({ filter }), setQ: (q) => fset({ q }) };
}

export function InboxPanel({ onCollapse, onSearch }) {
  const navigate = useNavigate();
  const { data } = useInbox();
  const f = useInboxFilter();
  const select = (it) => navigate(keyPath(it.key));
  const allSeen = () => { const keys = (data?.items || []).filter((x) => !x.seen).map((x) => x.key); if (keys.length) markSeen(keys, true); };
  return (
    <Panel title="Innboks" meta={data?.unread ? `${data.unread} uleste` : null} onCollapse={onCollapse} onSearch={onSearch} testId="panel-inbox" actions={<IconButton label="Marker alt som lest" onClick={allSeen} testId="panel-inbox-mark-all"><CheckCheck className="h-[17px] w-[17px]" strokeWidth={1.8} /></IconButton>}>
      <InboxList {...f} onSelect={select} />
    </Panel>
  );
}
